import useCustomTranslation from '@hooks/useCustomTranslation';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { CONFIRM_ACTION } from '@constants';
import { Nullable } from '@customTypes';
import CloseIcon from '@mui/icons-material/Close';
import { capitalizeFirstLetter } from '@utils/helpers';

import styles from './TranslationSetupDialog.module.scss';

interface ConfirmationDialogProps {
  action: Nullable<CONFIRM_ACTION>;
  handleClose: (action?: CONFIRM_ACTION) => void;
  handleProceed: (action: CONFIRM_ACTION) => void;
  isLoading: boolean;
}

export const ConfirmationDialog = ({ action, handleClose, handleProceed, isLoading }: ConfirmationDialogProps) => {
  const { getTranslation } = useCustomTranslation();

  if (!action) {
    return null;
  }

  const actionKey = action === CONFIRM_ACTION.RESET ? 'Generic.Reset' : 'Generic.Save';
  const descriptionKey =
    action === CONFIRM_ACTION.RESET ? 'CvaProj.addLanguage.confirmation.resetDescription' : 'SaveWsDlg.7';

  return (
    <Dialog
      open={!!action}
      keepMounted={false}
      aria-labelledby="confirmation-dialog"
      className={styles.addLanguageDialogContainer}
    >
      <DialogTitle id="confirmation-dialog" className={styles.dialogTitle}>
        <Box>
          {action === CONFIRM_ACTION.TAB_CHANGE
            ? getTranslation('CvaProj.SeveralChangesToSave')
            : `${capitalizeFirstLetter(getTranslation(actionKey, `buttons.${action}`))} ${getTranslation('CvaProj.addLanguage.confirmation.title', 'addLanguage.confirmation.title')}`}
        </Box>
        <IconButton aria-label="close" onClick={() => handleClose()}>
          <CloseIcon color="action" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {getTranslation(descriptionKey, `addLanguage.confirmation.${action}Description`)}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {action === CONFIRM_ACTION.TAB_CHANGE && (
          <Button variant="outlined" size="medium" onClick={() => handleClose(action)}>
            {getTranslation('Generic.Reset', 'buttons.reset')}
          </Button>
        )}
        <Button variant="outlined" size="medium" onClick={() => handleClose()}>
          {getTranslation('CvaInput.WizzCancel', 'buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={() => handleProceed(action)}
          disabled={isLoading}
          endIcon={isLoading && <CircularProgress />}
        >
          {getTranslation(actionKey, `buttons.${action}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
